import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
  FormHelperText,
  Divider,
  Alert,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { useRoles, groupRolesByCategory, CATEGORY_LABELS } from '../../hooks/useRoles';
import { useLocations } from '../../hooks/useUserLocations';
import type { RoleCategory } from '../../types';

interface StaffFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  roleIds: string[];
  locationIds: string[];
}

export const StaffCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const { data: locations, isLoading: isLoadingLocations } = useLocations();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<StaffFormData>({
    defaultValues: {
      roleIds: [],
      locationIds: [],
    },
  });

  const selectedRoleIds = watch('roleIds');

  // Group roles by category for display
  const rolesByCategory = roles ? groupRolesByCategory(roles) : null;

  // Get selected roles for display
  const selectedRoles = roles?.filter((r) => selectedRoleIds.includes(r.id)) || [];

  const createMutation = useMutation({
    mutationFn: async (data: StaffFormData) => {
      // Create the user (without roleId - roles assigned separately)
      const userData: any = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        password: data.password,
      };

      const response = await axiosInstance.post('/users', userData);
      const userId = response.data.id;

      // Assign roles
      if (data.roleIds && data.roleIds.length > 0) {
        await axiosInstance.post(`/permissions/users/${userId}/roles`, {
          roleIds: data.roleIds,
        });
      }

      // Assign locations if any selected
      if (data.locationIds && data.locationIds.length > 0) {
        await axiosInstance.post(`/permissions/users/${userId}/locations`, {
          locationIds: data.locationIds,
        });
      }

      return response.data;
    },
    onSuccess: () => {
      showMessage('Staff member created successfully', 'success');
      navigate('/app/staff');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create staff member', 'error');
    },
  });

  const onSubmit = (data: StaffFormData) => {
    createMutation.mutate(data);
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Add Staff Member
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Basic Information */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Basic Information" />
          <CardContent>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <TextField
                {...register('firstName', { required: 'First name is required' })}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                fullWidth
                label="First Name"
                autoFocus
              />

              <TextField
                {...register('lastName', { required: 'Last name is required' })}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                fullWidth
                label="Last Name"
              />

              <TextField
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                label="Email"
                type="email"
              />

              <TextField
                {...register('phone', { required: 'Phone is required' })}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                fullWidth
                label="Phone"
              />

              <TextField
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                fullWidth
                label="Password"
                type="password"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Role & Permissions */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Roles & Permissions"
            subheader="Assign one or more roles. Permissions are combined (UNION) from all assigned roles."
          />
          <Divider />
          <CardContent>
            {/* Multi-Role Selection */}
            <Controller
              name="roleIds"
              control={control}
              rules={{ required: 'At least one role is required' }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>Roles</InputLabel>
                  <Select
                    {...field}
                    multiple
                    label="Roles"
                    input={<OutlinedInput label="Roles" />}
                    disabled={isLoadingRoles}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((roleId) => {
                          const role = roles?.find((r) => r.id === roleId);
                          return (
                            <Chip
                              key={roleId}
                              label={role?.name || roleId}
                              size="small"
                              color={
                                role?.category === 'administrative'
                                  ? 'primary'
                                  : role?.category === 'operational'
                                  ? 'secondary'
                                  : 'default'
                              }
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {/* Group roles by category */}
                    {rolesByCategory &&
                      (['administrative', 'operational', 'combined'] as RoleCategory[]).flatMap((category) => {
                        const categoryRoles = rolesByCategory[category];
                        if (!categoryRoles?.length) return [];
                        return [
                          <ListSubheader key={`header-${category}`} sx={{ bgcolor: 'background.paper' }}>
                            {CATEGORY_LABELS[category]}
                          </ListSubheader>,
                          ...categoryRoles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                {role.name}
                                {role.isSystemDefault && (
                                  <Chip label="Default" size="small" variant="outlined" />
                                )}
                                {role.isAdmin && (
                                  <Chip label="Admin" size="small" color="error" />
                                )}
                              </Box>
                            </MenuItem>
                          )),
                        ];
                      })}
                  </Select>
                  {fieldState.error ? (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  ) : (
                    <FormHelperText>
                      Select multiple roles to combine their permissions. Use Ctrl/Cmd+Click to select multiple.
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Display selected roles info */}
            {selectedRoles.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedRoles.length} role{selectedRoles.length > 1 ? 's' : ''} selected:
                </Typography>
                {selectedRoles.map((role) => (
                  <Box key={role.id} sx={{ mb: 1 }}>
                    <strong>{role.name}</strong>
                    {role.description && `: ${role.description}`}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Category: {role.category || 'combined'} | {role.rolePermissions?.length || 0} permissions
                    </Typography>
                  </Box>
                ))}
              </Alert>
            )}

            {isLoadingRoles && (
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading roles...</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Location Access */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Location Access"
            subheader="Select which locations this staff member can access"
          />
          <Divider />
          <CardContent>
            <Controller
              name="locationIds"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Assigned Locations</InputLabel>
                  <Select
                    {...field}
                    multiple
                    input={<OutlinedInput label="Assigned Locations" />}
                    disabled={isLoadingLocations}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((locationId) => {
                          const location = locations?.find((l) => l.id === locationId);
                          return (
                            <Chip
                              key={locationId}
                              label={location?.name || locationId}
                              size="small"
                              color={location?.type === 'store' ? 'primary' : 'secondary'}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {locations?.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                        <Chip
                          label={location.type}
                          size="small"
                          sx={{ ml: 1 }}
                          variant="outlined"
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Staff will only see data from their assigned locations. Leave empty for access based on their default location.
                  </FormHelperText>
                </FormControl>
              )}
            />

            {isLoadingLocations && (
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading locations...</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Box display="flex" gap={2}>
          <Button
            type="submit"
            variant="contained"
            startIcon={createMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Staff Member'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/app/staff')}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
